import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Collapse,
  Group,
  Stack,
  Text,
} from '@mantine/core';
import { BookOpen, ChevronDown, ChevronUp } from 'react-feather';
import { ContentCard } from '../../../components/common/ContentCard';
import { RichTextContent } from '../../../components/common/RichTextEditor/RichTextContent';

const TITLE_COLOR = '#1A1A2E';

interface Props {
  /** Sanitized HTML authored by the CM on the Participant Pre-Session Work panel. */
  content: string;
  /**
   * MED-1115 — ISO timestamp when the participant marked this reviewed, or null.
   * Drives the "Reviewed" header check + the collapse-on-return seed.
   */
  reviewedAt: string | null;
  /** MED-1115 — persist the reviewed marker. The card collapses on success. */
  onMarkReviewed: () => Promise<void>;
}

/**
 * Reading / Tasks card (MED-984 / E3.1c — AC-RT1; polished + made completable
 * by MED-1115).
 *
 * Renders the CM-authored Reading / Tasks rich-text content, last in the
 * pre-session section order. The parent withholds this card entirely when
 * there's no authored content (AC-RT2), so it assumes non-empty content.
 *
 * MED-1115 — built on the shared `ContentCard` and made completable: a
 * bottom-right "Mark as Reviewed" primary button (modeled on the participant
 * post-session notes card, MED-1057) persists a reviewed marker; once reviewed,
 * the header shows a green "Reviewed" check and the section auto-collapses. It
 * seeds collapsed on a return visit when already reviewed (no flash) and allows
 * manual re-expand. Shared with peer-leader prep, so the treatment carries to
 * both roles.
 */
export function ReadingTasksCard({
  content,
  reviewedAt,
  onMarkReviewed,
}: Props) {
  const reviewed = reviewedAt != null;
  // Seed collapsed from the reviewed marker so an already-reviewed section loads
  // collapsed on return without flashing open (the only load-time collapse).
  const [collapsed, setCollapsed] = useState(() => reviewed);
  const [marking, setMarking] = useState(false);

  const handleMark = async () => {
    setMarking(true);
    try {
      await onMarkReviewed();
      setCollapsed(true);
    } finally {
      setMarking(false);
    }
  };

  return (
    <ContentCard
      variant="elevated"
      radius={16}
      padding="lg"
      contentGap={0}
      fill={false}
      header={
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="xs" align="center">
            <BookOpen size={18} color="#6B7280" aria-hidden />
            <Text fw={600} fz={17} c={TITLE_COLOR}>
              Reading / Tasks
            </Text>
            {reviewed && (
              // MED-1115 — same grail pill as the reflection status badge, next
              // to the title, for consistency across the pre-session sections.
              <Badge
                color="medleyGreen"
                variant="filled"
                radius="xl"
                tt="uppercase"
                styles={{
                  root: { letterSpacing: '0.6px' },
                  label: {
                    fontSize: 12,
                    fontWeight: 600,
                    transform: 'translateY(1px)',
                  },
                }}
              >
                Reviewed
              </Badge>
            )}
          </Group>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            onClick={() => setCollapsed(c => !c)}
            aria-label={
              collapsed ? 'Expand reading / tasks' : 'Collapse reading / tasks'
            }
            aria-expanded={!collapsed}
          >
            {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
          </ActionIcon>
        </Group>
      }
    >
      <Collapse in={!collapsed}>
        <Stack gap="md" mt="md">
          <RichTextContent content={content} />
          {!reviewed && (
            <Group justify="flex-end">
              <Button
                onClick={handleMark}
                loading={marking}
                variant="filled"
                color="dark"
                data-testid="reading-tasks-mark-reviewed"
              >
                Mark as Reviewed
              </Button>
            </Group>
          )}
        </Stack>
      </Collapse>
    </ContentCard>
  );
}

export default ReadingTasksCard;
